import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Section } from '~/eds/src/components/Section/Section';
import { slices } from '~/redux/slices';
import { Uuid } from '~/types/Uuid';

import { ReviewTestCases } from '../Review/ReviewTestCases';
import { PromptModelTestCase } from '../types/deprecated';
import { EvaluateSettings } from './EvaluateSettings';

interface Props {
  modelId: Uuid;
  testCases: PromptModelTestCase[];
  version: number;
  mode?: 'summary-only';
  isConfigDirty?: boolean;
  isExactTextMatch?: boolean;
  isTextField?: boolean;
  readOnly?: boolean;
  sectionTitle?: string;
  onToggleIsExactTextMatch?: (updatedEnableTextExactMatch: boolean) => void;
}

export const Evaluate = ({
  isConfigDirty,
  isExactTextMatch,
  isTextField,
  mode,
  modelId,
  readOnly,
  sectionTitle = '',
  testCases,
  version,
  onToggleIsExactTextMatch,
}: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const dismissedIncorrectStatusMessage = useSelector(
    slices.xRay.selectors.selectDismissedIncorrectStatusMessage,
  );
  const isViewingPastVersion = useSelector(
    slices.xRay.selectors.selectIsViewingPastVersion,
  );
  const reviewErrorCount = useSelector(
    slices.xRay.selectors.selectReviewIncorrectCount,
  );
  const isNewVersion = useSelector(slices.xRay.selectors.selectIsNewVersion);

  const enableTextExactMatch = isTextField;

  const valuesUpdatedTitle = intl.formatMessage({
    id: 'Evaluate.valuesUpdated',
    defaultMessage: 'Values Updated',
    description: 'Title for values updated status message',
  });

  if (!modelId || testCases.length === 0) {
    return null;
  }

  const getStatusMessage = () => {
    if (!readOnly) {
      if (isViewingPastVersion && !isConfigDirty) {
        return {
          status: 'ai' as const,
          message: intl.formatMessage({
            id: 'Evaluate.valuesBasedOnInstructions',
            defaultMessage:
              'Currently displayed values are based on the model instructions above.',
            description:
              'Message explaining that values are based on current model instructions',
          }),
          title: valuesUpdatedTitle,
        };
      } else if (isConfigDirty) {
        return {
          status: 'ai' as const,
          message: intl.formatMessage({
            id: 'Evaluate.clickSaveAndEvaluate',
            defaultMessage:
              'Please click on "Save and Evaluate" to see new results.',
            description:
              'Message prompting user to save and evaluate to see new results',
          }),
          title: intl.formatMessage({
            id: 'Evaluate.configurationUpdated',
            defaultMessage: 'Configuration Updated',
            description: 'Title for configuration updated status message',
          }),
        };
      } else if (isNewVersion) {
        return {
          status: 'ai' as const,
          message: intl.formatMessage({
            id: 'Evaluate.valuesUpdatedBasedOnNewInstructions',
            defaultMessage:
              'Currently displayed values have been updated based on the new model instructions above.',
            description:
              'Message explaining that values have been updated based on new model instructions',
          }),
          title: valuesUpdatedTitle,
        };
      } else if (
        !isViewingPastVersion &&
        !dismissedIncorrectStatusMessage &&
        reviewErrorCount >= 2
      ) {
        return {
          status: 'ai' as const,
          message: intl.formatMessage({
            id: 'Evaluate.editModelInstructions',
            defaultMessage:
              'Edit model instructions to improve model performance.',
            description:
              'Message suggesting to edit model instructions to improve performance',
          }),
          title: intl.formatMessage({
            id: 'Evaluate.editModelInstructionsTitle',
            defaultMessage: 'Edit Model Instructions',
            description: 'Title for edit model instructions status message',
          }),
          onDismiss: () =>
            dispatch(
              slices.xRay.actions.setDismissedIncorrectStatusMessage(true),
            ),
        };
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  };

  return (
    <Section
      description={
        readOnly
          ? undefined
          : intl.formatMessage({
              id: 'Evaluate.reviewAndRateDescription',
              defaultMessage:
                'Review and rate what the model found based on the instructions provided to the model.',
              description:
                'Description for the evaluate section explaining how to review and rate model results',
            })
      }
      actions={{
        actions: [],
        morePopover:
          enableTextExactMatch &&
          isExactTextMatch !== undefined &&
          typeof onToggleIsExactTextMatch === 'function'
            ? {
                content: (
                  <EvaluateSettings
                    isExactTextMatch={isExactTextMatch}
                    onToggleIsExactTextMatch={onToggleIsExactTextMatch}
                  />
                ),
              }
            : undefined,
      }}
      statusMessage={getStatusMessage()}
      title={sectionTitle}
    >
      <ReviewTestCases
        mode={mode}
        modelId={modelId}
        readOnly={isViewingPastVersion || readOnly}
        testCases={testCases}
        version={version}
      />
    </Section>
  );
};
